import React, { useState, useRef, ChangeEvent, FC, ReactNode } from 'react'
import Modal, { ModalProps } from 'components/modal'
import Button from 'components/button'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { validateSourceUrl, normalizeProfileUrl } from 'helpers/utils'
import { IUserObject, IFileParentResource } from 'types'
import Input from 'components/input'

interface EditInfoModalProps extends ModalProps {
  user: IUserObject
  updateUser: (user: IUserObject) => Promise<void>
}

interface RowProps {
  label: string
  children: ReactNode
}

const Row: FC<RowProps> = ({ label, children }) => (
  <div className="flex items-center mb-8">
    <div className="mr-5 w-[150px]">{label}</div>
    {children}
  </div>
)

const EditInfoModal: FC<EditInfoModalProps> = ({ id, open, setOpen, user, updateUser }) => {
  const { displayErrorMessage } = useApplicationStore()

  const [userDetails, setUserDetails] = useState<IUserObject>(user)
  const [avatarSrc, setAvatarSrc] = useState<string | undefined>(user.profilePicture || undefined)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSave = async (): Promise<void> => {
    const { name } = userDetails
    if (!name) return
    const normalized: IUserObject = {
      ...userDetails,
      website: normalizeProfileUrl(userDetails.website),
      twitter: normalizeProfileUrl(userDetails.twitter),
      linkedin: normalizeProfileUrl(userDetails.linkedin)
    }
    try {
      await updateUser(normalized)
      setOpen(false)
    } catch (err) {
      displayErrorMessage(err)
    }
  }

  const openFileDialog = () => fileInputRef.current?.click()

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const inputEl = e.target
    const file = inputEl.files?.[0]
    if (!file) return

    setAvatarSrc(URL.createObjectURL(file))

    try {
      const resources: IFileParentResource[] = [{ resource: 'user', publicId: user.publicId }]
      const { data } = await api.uploadFile(file, resources)
      setUserDetails((prev) => ({ ...prev, profilePicture: data.url }))
    } catch (err) {
      displayErrorMessage(err)
      setUserDetails((prev) => ({ ...prev, profilePicture: '' }))
      setAvatarSrc(undefined)
    }
    inputEl.value = ''
  }

  const handleImageRemove = () => {
    setUserDetails((prev) => ({ ...prev, profilePicture: '' }))
    setAvatarSrc(undefined)
  }

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Update Profile">
      <Row label="Name:">
        <Input
          required
          id="name-input"
          defaultValue={userDetails.name}
          onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
        />
      </Row>

      <Row label="Bio:">
        <textarea
          id="biography-input"
          defaultValue={userDetails.bio}
          onChange={(e) => setUserDetails({ ...userDetails, bio: e.target.value })}
        />
      </Row>

      <Row label="Picture:">
        <img
          className="object-cover rounded-full w-[100px] h-[100px]"
          src={validateSourceUrl(avatarSrc)}
          alt={userDetails.name}
        />

        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageChange} className="hidden" />

        <Button
          className="ml-5"
          internalType="danger"
          onClick={handleImageRemove}
          disabled={!userDetails.profilePicture}
        >
          Remove
        </Button>
        <Button className="ml-[15px]" onClick={openFileDialog}>
          Edit
        </Button>
      </Row>

      {(
        [
          ['Organisation', 'organisation'],
          ['Location', 'location'],
          ['Website', 'website'],
          ['University', 'university'],
          ['Degree', 'universityDegree'],
          ['Twitter URL', 'twitter'],
          ['LinkedIn URL', 'linkedin']
        ] as const
      ).map(([label, key]) => (
        <Row key={key} label={`${label}:`}>
          <Input
            id={`${key}-input`}
            defaultValue={userDetails[key]}
            onChange={(e) => setUserDetails({ ...userDetails, [key]: e.target.value })}
          />
        </Row>
      ))}

      <div className="flex justify-end mt-5 gap-2.5">
        <Button internalType="danger" onClick={() => setOpen(false)} data-cy="cancel-update-button">
          Cancel
        </Button>
        <Button internalType="accept" onClick={handleSave} data-cy="submit-update-button">
          Save Profile
        </Button>
      </div>
    </Modal>
  )
}

export default EditInfoModal
